/** Signed-in chrome: terms gate first, then the entitlement (spec 2026-09-19 §9) — a workspace
 * that may not spend is shown the chooser unless it is reading what it already made — then
 * subjects, a subject's documents, a book's review or its deck, the subject's exam format,
 * a paper, the account page, or the return from checkout — by pathname. */
import { useEffect, useMemo, useState } from "react";
import type { ApiFailure } from "../api/client";
import { FailedPage, LoadingPage } from "../states/Page";
import { makeApi, type Entitlement, type Me } from "../api/api";
import { AccountScreen } from "../billing/AccountScreen";
import { openCheckout } from "../billing/paddle";
import { PlanBadge } from "../billing/PlanBadge";
import { ReturnScreen } from "../billing/ReturnScreen";
import { SubscribeScreen } from "../billing/SubscribeScreen";
import { brand } from "../brand";
import type { AppConfig } from "../config";
import { FeedbackPanel } from "../feedback/FeedbackPanel";
import { PaperScreen } from "../papers/PaperScreen";
import { ProfileScreen } from "../profile/ProfileScreen";
import { BookScreen } from "../review/BookScreen";
import { StudyScreen } from "../study/StudyScreen";
import { SubjectsScreen } from "../subjects/SubjectsScreen";
import { TermsScreen } from "../terms/TermsScreen";
import { Footer } from "../site/Footer";
import { HOME } from "../site/Site";
import "../styles/site.css";
import { SubjectScreen } from "../upload/SubjectScreen";
import { AccountMenu } from "./AccountMenu";
import { useAuth } from "./AuthProvider";
import { navigate, useRoute } from "./router";

/** Read-only still reads: the library, a subject, its deck and its papers stay open to a
 * workspace that may not spend; the spend points 402 to the chooser (spec §6.5). */
const READABLE = [/^\/subjects$/, /^\/subjects\/[^/]+$/, /^\/study\//, /^\/papers\//, /^\/books\//];

export function Shell({ cfg }: { cfg: AppConfig }) {
  const { session, signOut, refresh } = useAuth();
  const api = useMemo(() => makeApi(cfg, session.accessToken), [cfg, session.accessToken]);
  const route = useRoute();
  const [me, setMe] = useState<Me | null>(null);
  // The quota changes on a route change (confirm holds pages) and when a document settles
  // (an ingest settles or releases them); either bumps the badge.
  const [settled, setSettled] = useState(0);
  const [ent, setEnt] = useState<Entitlement | null>(null);
  // The entitlement changes when the webhook lands or a code is redeemed; a focus (back from
  // Paddle's portal, another tab) or a bump re-reads it, so the flip needs no reload.
  const [entKey, setEntKey] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // A first load that failed; once a value is in hand, a later failure keeps the old one.
  const [failed, setFailed] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    void api.me().then((r) => (r.ok ? setMe(r.data) : setFailed(r)));
  }, [api, attempt]);
  useEffect(() => {
    if (!me?.terms.accepted) return;
    void api.billingMe().then((r) => (r.ok ? setEnt(r.data) : setFailed(r)));
  }, [api, me, route, settled, entKey, attempt]);
  const retry = () => {
    setFailed(null);
    setAttempt((n) => n + 1);
  };
  useEffect(() => {
    const on = () => setEntKey((n) => n + 1);
    window.addEventListener("focus", on);
    return () => window.removeEventListener("focus", on);
  }, []);

  if (!me) return failed ? <FailedPage failure={failed} onRetry={retry} /> : <LoadingPage />;
  if (!me.terms.accepted) {
    return (
      <TermsScreen
        version={me.terms.current}
        acceptTerms={api.acceptTerms}
        onAccepted={async () => {
          await refresh(); // the fresh token carries tos_accepted=true
          setMe({ ...me, terms: { ...me.terms, accepted: true } });
        }}
      />
    );
  }
  if (!ent) return failed ? <FailedPage failure={failed} onRetry={retry} /> : <LoadingPage />;
  const promo = new URLSearchParams(window.location.search).get("promo");
  const readable = READABLE.some((re) => re.test(route)) || route === "/account";
  // First visit lands on the chooser, not an empty library whose first click is a 402.
  const firstRun = ent.reason === "never_subscribed" && route === HOME;
  const chooser =
    route === "/subscribe" || (!ent.active && !readable && route !== "/billing/return") || firstRun;
  const bump = () => setEntKey((n) => n + 1);
  const toChooser = () => navigate("/subscribe");
  const profileMatch = /^\/subjects\/([^/]+)\/profile$/.exec(route);
  const paperMatch = /^\/papers\/([^/]+)$/.exec(route);
  const subjectMatch = /^\/subjects\/([^/]+)$/.exec(route);
  const bookMatch = /^\/books\/([^/]+)$/.exec(route);
  // Fix a ready book's chapters (bug fix 1, spec 7), optionally scrolled to one of them.
  const fixMatch = /^\/books\/([^/]+)\/fix(?:\/(\d+))?$/.exec(route);
  const studyMatch = /^\/study\/([^/]+)$/.exec(route);
  return (
    <>
      <header className="bar">
        <span className="wordmark">{brand.wordmark}</span>
        <nav aria-label="Main">
          {/* On a phone the links wrap out of view (design v2 § 7); the account menu has them. */}
          <span className="bar__links">
            <button
              type="button"
              className="bar__link"
              aria-current={route === HOME || route === "/subjects" ? "page" : undefined}
              onClick={() => navigate("/subjects")}
            >
              Subjects
            </button>
            <FeedbackPanel
              api={api}
              path={route}
              open={feedbackOpen}
              onOpenChange={setFeedbackOpen}
            />
          </span>
          <PlanBadge entitlement={ent} onOpen={() => navigate("/account")} />
          <AccountMenu
            email={session.email}
            entitlement={ent}
            onSubjects={() => navigate("/subjects")}
            onFeedback={() => setFeedbackOpen(true)}
            onAccount={() => navigate("/account")}
            onSignOut={signOut}
          />
        </nav>
      </header>
      {chooser ? (
        <SubscribeScreen
          api={api}
          reason={ent.reason === "ok" ? "never_subscribed" : ent.reason}
          workspaceId={me.workspace_id}
          promo={promo}
          trialOpen={cfg.trialOpen}
          openCheckout={(input) => openCheckout(cfg, input)}
          onRedeemed={() => {
            bump();
            navigate("/subjects");
          }}
        />
      ) : route === "/billing/return" ? (
        <ReturnScreen
          api={api}
          onActive={() => {
            bump();
            navigate("/subjects");
          }}
        />
      ) : route === "/account" ? (
        <AccountScreen api={api} entitlement={ent} email={session.email} onSubscribe={toChooser} />
      ) : profileMatch ? (
        <ProfileScreen
          api={api}
          subjectId={profileMatch[1] ?? ""}
          onConfirmed={() => navigate(`/subjects/${profileMatch[1] ?? ""}`)}
        />
      ) : paperMatch ? (
        <PaperScreen
          api={api}
          paperId={paperMatch[1] ?? ""}
          onBack={(subjectId) => navigate(`/subjects/${subjectId}`)}
        />
      ) : studyMatch ? (
        <StudyScreen
          api={api}
          bookId={studyMatch[1] ?? ""}
          onBack={(subjectId) => navigate(`/subjects/${subjectId}`)}
          onFixChapter={(n) => navigate(`/books/${studyMatch[1] ?? ""}/fix/${n}`)}
        />
      ) : fixMatch ? (
        <BookScreen
          api={api}
          bookId={fixMatch[1] ?? ""}
          mode="fix"
          focusChapter={fixMatch[2] ? Number(fixMatch[2]) : undefined}
          onConfirmed={(book) => navigate(`/subjects/${book.subject_id}`)}
          onCancel={(book) => navigate(`/subjects/${book.subject_id}`)}
        />
      ) : bookMatch ? (
        <BookScreen
          api={api}
          bookId={bookMatch[1] ?? ""}
          onConfirmed={(book) => navigate(`/subjects/${book.subject_id}`)}
        />
      ) : subjectMatch ? (
        <SubjectScreen
          api={api}
          subjectId={subjectMatch[1] ?? ""}
          onReview={(bookId) => navigate(`/books/${bookId}`)}
          onFix={(bookId) => navigate(`/books/${bookId}/fix`)}
          onStudy={(bookId) => navigate(`/study/${bookId}`)}
          onProfile={(id) => navigate(`/subjects/${id}/profile`)}
          onPaper={(id) => navigate(`/papers/${id}`)}
          onBack={() => navigate("/subjects")}
          onSettled={() => setSettled((n) => n + 1)}
          onNotEntitled={toChooser}
        />
      ) : (
        <SubjectsScreen
          api={api}
          onOpen={(id) => navigate(`/subjects/${id}`)}
          onNotEntitled={toChooser}
        />
      )}
      <Footer />
    </>
  );
}
